import { useEffect, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { X, Lock } from "lucide-react";
import { stripePromise } from "@/lib/stripe";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CardForm = ({ booking, onSaved, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (error) {
        toast.error(error.message || "Card could not be saved");
        return;
      }
      const pm = setupIntent?.payment_method;
      if (!pm) throw new Error("no payment method");
      const { data } = await api.post(
        "/admin/cards/confirm",
        { booking_id: booking.id, payment_method_id: pm },
        { withCredentials: true }
      );
      toast.success(`Card saved: ${data.card.brand} •••• ${data.card.last4}`);
      onSaved(data.card);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the card — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      <button
        data-testid="card-modal-save-button"
        type="submit"
        disabled={saving || !stripe}
        className="mt-5 w-full rounded-xl bg-brand-navy px-5 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-white transition-transform hover:scale-[1.02] disabled:opacity-50"
      >
        {saving ? "Saving securely…" : "Save card"}
      </button>
    </form>
  );
};

export const CardModal = ({ booking, onSaved, onClose }) => {
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    api
      .post("/admin/cards/setup-intent", { booking_id: booking.id }, { withCredentials: true })
      .then((r) => setClientSecret(r.data.client_secret))
      .catch(() => {
        toast.error("Couldn't start the secure card form — try again.");
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="card-modal"
        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-extrabold text-brand-navy">
              Card for {booking.name}
            </h3>
            <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
              <Lock className="h-3.5 w-3.5 text-emerald-600" /> Secured by Stripe — the card number never touches our servers
            </p>
          </div>
          <button data-testid="card-modal-close" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-5">
          {clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret, paymentMethodCreation: "manual" }}>
              <CardForm booking={booking} onSaved={onSaved} onClose={onClose} />
            </Elements>
          ) : (
            <p className="py-8 text-center text-sm font-semibold text-slate-400">Loading secure form…</p>
          )}
        </div>
      </div>
    </div>
  );
};
